import { useTranslation } from 'react-i18next';
import { Dices } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useResumableMatch } from '@/contexts/ResumableMatchContext';
import { useStartMatch } from '@/hooks/useStartMatch';

/**
 * LA BANDA SULLA HUB — cio' che il giocatore ha in sospeso, dove lo vede.
 *
 * ═══ PERCHE' ESISTE, e perche' NON bastava #186 ═══
 * `#186` ha alzato la CHIAMATA all'avvio dell'app (`ResumableMatchProvider` in
 * `AppShell`), non l'AVVISO: l'unico consumatore restava dentro
 * `BackgammonLobby` ⇒ **chi apre la Hub e non entra nella sezione backgammon
 * continuava a non vedere niente**, che era letteralmente il difetto da
 * chiudere. Questo componente e' il consumatore che mancava, e sta sulla Hub
 * perche' la Hub e' dove si atterra — non dietro una sezione di gioco.
 * [Decisione di Luca, 2026-09-07 20:21:14Z, testuale: «(a) Banda Hub + fix
 * accettazione insieme», in risposta a «cosa deve vedere chi ha un appuntamento
 * in attesa?». La scelta era fra la banda e una pastiglia del match attivo: ha
 * scelto la banda.]
 *
 * ═══ 🔴 LA FRASE, e perche' non e' «X ti sta aspettando» ═══
 * [M or56 2026-09-07, `qgammon-gameserver/src/game/payloads.ts:52` @ origin/main
 * · misura di `tma12` sui due profili di collaudo, stesso appuntamento, stesso
 * istante: **ciascuno dei due leggeva che l'altro lo stava aspettando**]
 * La riga di appuntamento espone i posti con `identity()`, che **non porta
 * `connected`** (la riga di una partita VIVA si': e' l'asimmetria che inganna).
 * ⇒ Il client **non puo' sapere chi dei due manca**. Finche' nessuno e' entrato,
 * «X ti sta aspettando» e' vero per entrambi e falso sul fatto: **nessuno sta
 * aspettando nessuno, l'appuntamento e' soltanto aperto.**
 * ⇒ Si dice cio' che si sa — **chi ha invitato chi**, che e' un fatto compiuto e
 * arriva in `createdBy` — e si aggiunge la sola regola sempre vera: la partita
 * comincia quando ci sono tutti e due (ADR-0114, `syncPresence`).
 * ⛔ Non reintrodurre una frase sulla presenza finche' la riga non porta un
 * campo che la dichiari: non e' una questione di gusto, e' che il dato non c'e'.
 *
 * ⚠️ NON e' game-conditional. La sezione backgammon lo e', questa no: il senso
 * della banda e' raggiungere chi in quella sezione non entra. L'unica condizione
 * e' quella del provider (`featureBackgammonEnabled`), che quando e' spento
 * rende `null` — e con la feature spenta le rotte `/backgammon/*` non esistono
 * nemmeno, quindi non c'e' nulla su cui atterrare.
 *
 * ═══ 🔴 `data-kind` — LO STATO NEL DOM, e non e' un attributo per i test ═══
 * Senza di lui chi guarda questa banda da fuori puo' distinguere un APPUNTAMENTO
 * da una PARTITA VIVA **soltanto leggendo la frase** — e una guardia costruita
 * sul testo puo' avere il **dominio vuoto** senza che nessuno se ne accorga.
 * E' successo: `tools/tma-e2e/scenarios/appuntamento-banda.mjs` vieta «in corso»
 * **e** «appuntamento/invito» nella stessa riga, ma le copy di `live` non nominano
 * l'appuntamento, quindi la congiunzione non si chiude su **nessuna** delle sei
 * stringhe che questa banda sa produrre — **nemmeno sul difetto che cerca**
 * («Hai una partita in corso con X» mostrato su un appuntamento).
 * [M `tma-torre-review-orc`, 2026-09-08, sulle copy vere di entrambe le lingue.
 * Controllo positivo: su un testo costruito a mano che contiene entrambe le parti
 * la guardia **scatta** ⇒ la regex funziona, era il dominio a essere vuoto. Sono
 * due diagnosi opposte con lo stesso sintomo, e senza il controllo positivo si
 * correggerebbe la regex invece della causa.]
 * 🔑 La causa non stava nello scenario: **la figlia deduceva dal testo un dato che
 * la madre non esponeva.** Con questo attributo la guardia diventa «se
 * `data-kind` e' `waiting`, la frase non dice "in corso"» — un'asserzione che puo'
 * diventare rossa, che e' l'unica cosa che distingue una guardia da una cerimonia.
 * ⛔ Il valore e' lo STATO, mai la frase: la copy resta libera di cambiare.
 */
export function ResumableBanner() {
  const { t } = useTranslation('backgammon');
  const resumable = useResumableMatch();
  const startMatch = useStartMatch();
  const haptics = useHaptics();

  if (!resumable) return null;

  const { kind, initiator, opponentName } = resumable;
  // Due stati, due frasi, mai un ternario dentro una frase sola: «hai una
  // partita in corso» su un appuntamento sarebbe falso, ed e' il difetto per cui
  // tutto questo esiste.
  const titleKey =
    kind === 'live' ? 'resume.live.title' : `resume.waiting.${initiator}.title`;

  return (
    <button
      type="button"
      data-testid="resumable-banner"
      data-kind={kind}
      onClick={() => {
        haptics.impact('medium');
        startMatch({
          game: 'qgammon',
          matchId: resumable.matchId,
          // ⚠️ La prospettiva viaggia SEMPRE: senza, il tabellone ripiega su 'W'
          // e meta' delle volte si vede dal lato sbagliato.
          perspective: resumable.perspective,
          // E lo stato con lei: e' cio' che impedisce alla schermata di
          // annunciare «In gioco» al primo disegno di un appuntamento.
          status: kind === 'waiting' ? 'waiting' : 'active',
        });
      }}
      className="flex w-full shrink-0 items-center gap-3 rounded-xl border border-brand/40 bg-brand/10 px-3 py-2.5 text-left"
    >
      <Dices aria-hidden className="h-5 w-5 shrink-0 text-brand" />
      {/* ⛔ NIENTE `truncate` qui, e non e' una preferenza: misurato a 390 px con
          un nome CORTO («GeminiZ»), la riga usciva come «Hai accettato l'invito
          di Gemi…» e l'avviso come «La partita inizia quando ci siete tutti e
          …». Un avviso troncato a meta' della frase che lo rende vero e' peggio
          di nessun avviso — e col nome dentro, troncare taglia proprio la parola
          che dice CON CHI. La banda ha due righe e le usa. */}
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold leading-snug text-fg">
          {t(titleKey, { name: opponentName })}
        </span>
        {/* La riga che regge da sola qualunque cosa non sappiamo: vale sia se
            manca lui, sia se manchiamo noi, sia se mancano entrambi. */}
        <span className="mt-0.5 block text-[11px] leading-snug text-fg-muted">
          {t(kind === 'live' ? 'resume.live.hint' : 'resume.waiting.hint')}
        </span>
      </span>
      <span className="shrink-0 text-xs font-semibold text-brand">
        {t(kind === 'live' ? 'resume.live.cta' : 'resume.waiting.cta')}
      </span>
    </button>
  );
}
